﻿import React from "react";
import { useSelector } from "react-redux";
import { updateSlideTemplates, saveCurrentPageTemplates } from "../../store/storyline/actions";
import CodeMirror, { type BasicSetupOptions } from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { css } from "@codemirror/lang-css";
import { indentUnit } from "@codemirror/language";
import { EditorView } from "@codemirror/view";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { Button, TabPanel, Tab, Tabs, Toolbar } from "../../shared/components";
import { eagerParsing, measureOnScroll } from "../../shared/utils/codemirror";
import { RootState, useThunkDispatch } from "../../store";
import { TemplateDefinition } from "../../store/storyline/types";


function shortenTemplateName(templateName: string) {
    return templateName.match(/((?:[^\/]+\/){0,1}[^\/]+)$/)?.[1] ?? templateName;
}

function Templates() {
    const templates = useSelector((root: RootState) => root.storyline?.storyline?.templates ?? {});
    const currentPageTemplate = useSelector((root: RootState) => root.storyline.currentParagraph?.templateName);
    const [selectedTabValue, setSelectedTabValue] = React.useState(currentPageTemplate);

    const handleTabChange = React.useCallback((_event: React.ChangeEvent<{}>, newValue: string) => {
        setSelectedTabValue(newValue);
    }, [setSelectedTabValue]);

    return (
        <div className="tabs-container">
            <Tabs
                variant="scrollable"
                visibleScrollbar
                scrollButtons={false}
                style={{ backgroundColor: "var(--colors-blue-1)" }}
                value={selectedTabValue}
                onChange={handleTabChange}
            >
                {
                    Object.keys(templates).map(templateName => <Tab label={shortenTemplateName(templateName)} value={templateName} wrapped title={templateName} />)
                }
            </Tabs>

            {
                Object.keys(templates).map((templateName) =>
                    <TabPanel key={templateName} selectedValue={selectedTabValue} value={templateName}>
                        <Template key={templateName} templateName={templateName} templates={templates} />
                    </TabPanel>
                )
            }
        </div>
    );
};

interface TemplateProps {
    templateName: string,
    templates: Record<string, TemplateDefinition>
}

// Extensions/setup must be referentially stable — @uiw/react-codemirror reconfigures the editor
// whenever these props change identity...
const JSX_EXTENSIONS = [javascript({ jsx: true }), indentUnit.of("    "), eagerParsing, measureOnScroll];
const CSS_EXTENSIONS = [css(), indentUnit.of("    "), eagerParsing, measureOnScroll];
const EDITOR_SETUP: BasicSetupOptions = { lineNumbers: true, foldGutter: true };

function Template(props: TemplateProps) {
    const { templateName, templates } = props;
    const template = templates[templateName] ?? { jsx: "", css: "" };
    const jsxEditorRef = React.useRef<EditorView>(null);
    const cssEditorRef = React.useRef<EditorView>(null);
    const dispatch = useThunkDispatch();

    const jsxEditorDidMount = React.useCallback((view: EditorView) => {
        jsxEditorRef.current = view;
        setTimeout(() => view.dispatch({ selection: { anchor: 0 }, scrollIntoView: true }));
    }, []);
    const cssEditorDidMount = React.useCallback((view: EditorView) => {
        cssEditorRef.current = view;
        setTimeout(() => view.dispatch({ selection: { anchor: 0 }, scrollIntoView: true }));
    }, []);

    const applyTemplateChange = React.useCallback(() => {
        dispatch(updateSlideTemplates({ [templateName]: { jsx: jsxEditorRef.current.state.doc.toString(), css: cssEditorRef.current.state.doc.toString() } }));
    }, [templateName]);

    const saveTemplate = React.useCallback(() => {
        dispatch(updateSlideTemplates({ [templateName]: { jsx: jsxEditorRef.current.state.doc.toString(), css: cssEditorRef.current.state.doc.toString() } }));
        dispatch(saveCurrentPageTemplates({ ...templates, [templateName]: { jsx: jsxEditorRef.current.state.doc.toString(), css: cssEditorRef.current.state.doc.toString() } }));
    }, [templateName, templates]);

    return (
        <div className="template-panel">
            <CodeMirror
                value={template.jsx}
                theme={vscodeDark}
                extensions={JSX_EXTENSIONS}
                basicSetup={EDITOR_SETUP}
                onCreateEditor={jsxEditorDidMount}
            />
            <div className="css-heading">CSS</div>
            <CodeMirror
                value={template.css || "// Custom CSS goes here..."}
                theme={vscodeDark}
                extensions={CSS_EXTENSIONS}
                basicSetup={EDITOR_SETUP}
                onCreateEditor={cssEditorDidMount}
            />

            <Toolbar className="d-flex flex-row align-items-center justify-content-center gap-2">
                <Button color="secondary" variant="contained" onClick={applyTemplateChange}>Apply Changes</Button>
                <Button color="success" variant="contained" className="save-changes" onClick={saveTemplate}>Save Template</Button>
            </Toolbar>
        </div >
    );

}

export default React.memo(Templates);